import type {ContentEditorialDto} from '@devmil/contracts';
import {useId, useState} from 'react';

import {
  EDITORIAL_CRITERION_LABEL,
  EDITORIAL_RECOMMENDATION_CLASS,
  EDITORIAL_RECOMMENDATION_LABEL,
  EDITORIAL_UNAVAILABLE_LABEL,
  formatDateTime,
} from '../../lib/format.js';

/**
 * O parecer do editor sobre o TEXTO, na revisão individual.
 *
 * ## O escopo, dito no título
 *
 * Ele se chamava "Parecer editorial" e cobria só o texto. Enquanto o parecer da
 * mídia não chegava à tela, o nome genérico parecia inofensivo; ao lado do
 * bloco da mídia, ele seria a fonte da confusão que o achado F11 descreve.
 * Agora ele diz "Parecer do texto", e a linha de rodapé repete o alcance junto
 * com a data e as versões.
 *
 * ## A frase que não é enfeite
 *
 * "O parecer informa. Quem aprova é você." Ela está na tela, e não só num
 * comentário de código, porque o risco real deste recurso é de LEITURA: um
 * bloco com nota, recomendação e semáforo, logo acima dos botões de decisão,
 * convida a ser lido como veredito. Ele não é: quem bloqueia peça são os sete
 * portões, logo acima, e quem libera é o dono, com passkey.
 *
 * ## Ausente NUNCA vira aprovado
 *
 * Sem parecer, o bloco desenha o MOTIVO, e não um espaço em branco. Espaço em
 * branco numa tela de aprovação é lido como "não há nada de errado", e a
 * diferença entre "o editor não achou defeito" e "ninguém olhou" é a diferença
 * entre as duas decisões que o dono pode tomar.
 *
 * ## Curto por padrão, detalhe sob demanda
 *
 * O bloco curto traz recomendação, nota, mudança principal e pendência. As
 * cinco notas e os achados ficam atrás de um botão: a revisão individual já
 * carrega mídia, legenda e sete portões, e mais quinze linhas abertas por
 * padrão empurrariam os botões de decisão para fora da primeira dobra.
 */
/**
 * O que a tela diz sobre as reescritas, e por que não é uma frase só.
 *
 * Ela dizia "Corrigida uma vez", porque só existia uma correção possível. Com o
 * laço (2026-09-07) existem duas perguntas, e as duas importam para quem aprova:
 *
 * - quantas reescritas a peça recebeu, que é o que explica o gasto dela;
 * - se o texto na tela É uma delas. Quando todas as reescritas ficaram piores
 *   que o original, o que está na tela é o original, e escrever "corrigida"
 *   sobre ele seria dizer que alguém mexeu num texto que ninguém mexeu.
 *
 * O plural sai à mão porque o painel não carrega biblioteca de idioma, e uma
 * dependência inteira para escolher entre "vez" e "vezes" seria peso sem ganho.
 */
const rotuloDaCorrecao = (editorial: {
  corrected: boolean;
  correctionAttempts: number;
}): string => {
  const vezes =
    editorial.correctionAttempts === 1
      ? 'uma vez'
      : `${editorial.correctionAttempts} vezes`;
  return editorial.corrected
    ? `Reescrita ${vezes}`
    : `Reescrita ${vezes}, ficou o texto original`;
};

export const EditorialOpinion = ({
  editorial,
}: {
  editorial: ContentEditorialDto;
}) => {
  const headingId = useId();
  const detalheId = useId();
  const [aberto, setAberto] = useState(false);

  if (editorial.availability === 'unavailable') {
    return (
      <section className="dm-editorial" aria-labelledby={headingId}>
        <h4 id={headingId}>Parecer do texto</h4>
        <p className="dm-editorial-vazio">
          {EDITORIAL_UNAVAILABLE_LABEL[editorial.reason]}
        </p>
      </section>
    );
  }

  return (
    <section className="dm-editorial" aria-labelledby={headingId}>
      <h4 id={headingId}>Parecer do texto</h4>
      <p className="dm-editorial-veredito">
        <strong
          className={EDITORIAL_RECOMMENDATION_CLASS[editorial.recommendation]}
        >
          {EDITORIAL_RECOMMENDATION_LABEL[editorial.recommendation]}
        </strong>
        <span>{`${editorial.total} de 20`}</span>
        {editorial.correctionAttempts === 0 ? null : (
          <span>{rotuloDaCorrecao(editorial)}</span>
        )}
      </p>
      {editorial.mainChange === null ? null : (
        <p className="dm-editorial-mudanca">{editorial.mainChange}</p>
      )}
      {editorial.pendency === null ? null : (
        <p className="dm-editorial-pendencia">
          {`Pendência ${
            editorial.pendency.severity === 'critical' ? 'crítica' : 'menor'
          }: ${editorial.pendency.text}`}
        </p>
      )}
      <p className="dm-editorial-aviso">
        O parecer informa. Quem aprova é você.
      </p>
      <button
        type="button"
        className="dm-editorial-toggle"
        aria-expanded={aberto}
        aria-controls={detalheId}
        onClick={() => setAberto((valor) => !valor)}
      >
        {aberto ? 'Ocultar detalhes' : 'Ver detalhes'}
      </button>
      <div id={detalheId} hidden={!aberto}>
        <ul
          className="dm-editorial-notas"
          aria-label="Cinco dimensões da régua"
        >
          {editorial.criteria.map((criterio) => (
            <li key={criterio.id}>
              <span>{EDITORIAL_CRITERION_LABEL[criterio.id]}</span>
              <strong>{`${criterio.score}/4`}</strong>
            </li>
          ))}
        </ul>
        {editorial.findings.length === 0 ? (
          <p className="dm-editorial-vazio">Sem achados.</p>
        ) : (
          <ul className="dm-editorial-achados" aria-label="Achados do editor">
            {editorial.findings.map((achado, indice) => (
              <li key={`${achado.field}-${indice}`}>
                <span className="dm-editorial-campo">{achado.field}</span>
                {achado.issue}
              </li>
            ))}
          </ul>
        )}
        {/*
          Escopo, data e versões, que o plano pede em cada parecer. O escopo
          entrou porque, com dois pareceres na tela, "qual dos dois é este" tem
          que ser respondível sem contar linhas para cima.
        */}
        <p className="dm-editorial-versao">
          {`texto · ${formatDateTime(editorial.evaluatedAt)} · ${
            editorial.rubricVersion
          } · ${editorial.promptVersion} · ${editorial.model}`}
        </p>
      </div>
    </section>
  );
};
